// Content data for each Insurance product sub-page

export const INSURANCE_PRODUCTS = {
  "life-insurance": {
    slug: "life-insurance",
    parentSlug: "/insurance",
    parentLabel: "Insurance",
    eyebrow: "Insurance Solutions",
    title: "Life Insurance",
    tagline: "Protection today, and a legacy for tomorrow.",
    intro:
      "Life insurance is the foundation of family financial security. Whether you need term coverage during your working years or permanent protection that also builds cash value, we'll walk through your options in plain English — no pressure.",
    whoFor: [
      "Parents wanting to ensure kids are cared for",
      "Homeowners protecting a mortgage",
      "Adults looking to lock in low rates while young and healthy",
      "Veterans transitioning off SGLI",
    ],
    benefits: [
      "Tax-free death benefit for your beneficiaries",
      "Permanent options that build cash value",
      "Coverage that fits your budget today",
      "Peace of mind, guaranteed by top-rated carriers",
    ],
    faqs: [
      { q: "Term vs. whole life — which is better?", a: "Neither is universally better. Term is affordable and simple for temporary needs. Whole life is more expensive but builds cash value and lasts a lifetime. We'll help you weigh both against your goals." },
      { q: "Do I need a medical exam?", a: "Sometimes. Many products today offer no-exam or accelerated underwriting, especially for younger and healthier applicants." },
    ],
  },
  "final-expense": {
    slug: "final-expense",
    parentSlug: "/insurance",
    parentLabel: "Insurance",
    eyebrow: "Insurance Solutions",
    title: "Final Expense",
    tagline: "Simplified whole life coverage to protect your family from end-of-life costs.",
    intro:
      "Funerals, medical bills, and outstanding debts can add up fast. Final expense insurance is a small, permanent policy designed to give your family cash quickly when they need it most.",
    whoFor: [
      "Seniors on a fixed income",
      "Adults who want to spare loved ones the burden of arrangements",
      "Anyone who has been declined for larger policies",
    ],
    benefits: [
      "Simplified underwriting — often no medical exam",
      "Coverage typically $5,000–$50,000",
      "Fixed premiums that never go up",
      "Cash value that grows over time",
    ],
    faqs: [
      { q: "Can I qualify with health issues?", a: "In most cases, yes. Final expense policies use simplified underwriting and are designed to be accessible." },
      { q: "How quickly is the benefit paid?", a: "Typically within days of a claim, once documentation is provided." },
    ],
  },
  "mortgage-protection": {
    slug: "mortgage-protection",
    parentSlug: "/insurance",
    parentLabel: "Insurance",
    eyebrow: "Insurance Solutions",
    title: "Mortgage Protection",
    tagline: "Keep your family in the home you built for them.",
    intro:
      "Mortgage protection is life insurance designed to pay off (or make payments on) your home if you pass away or become disabled. It's peace of mind for the biggest purchase most families ever make.",
    whoFor: [
      "New homeowners",
      "Families with a single primary earner",
      "Anyone wanting to guarantee the home stays with their loved ones",
    ],
    benefits: [
      "Coverage aligned with your mortgage balance",
      "Options for disability and critical illness riders",
      "Return-of-premium options available",
      "Fast-track underwriting",
    ],
    faqs: [
      { q: "Is this different from PMI?", a: "Yes. PMI protects your lender if you default. Mortgage protection protects your family." },
      { q: "Does the payout go to the bank?", a: "Not necessarily. The benefit is paid to your beneficiaries, who decide how to use it — pay the mortgage, refinance, or otherwise." },
    ],
  },
  "income-protection": {
    slug: "income-protection",
    parentSlug: "/insurance",
    parentLabel: "Insurance",
    eyebrow: "Insurance Solutions",
    title: "Income Protection",
    tagline: "If something happens to you, your paycheck doesn't have to stop.",
    intro:
      "Your ability to earn is one of your most valuable assets. Income protection provides monthly benefits that replace part of your income if you become unable to work due to disability or serious illness.",
    whoFor: [
      "Working professionals with dependents",
      "Self-employed and small business owners",
      "Anyone whose family depends on their paycheck",
    ],
    benefits: [
      "Monthly benefit that replaces a portion of income",
      "Short-term and long-term options",
      "Coverage for accidents and illness",
      "Custom benefit periods and elimination periods",
    ],
    faqs: [
      { q: "Isn't this covered by my employer?", a: "Employer coverage often replaces a smaller portion of income than you'd expect, and it usually ends if you leave the job. A private policy travels with you." },
      { q: "How is 'disability' defined?", a: "Definitions vary by product. We'll walk through the differences — 'own occupation' vs. 'any occupation' matters a lot." },
    ],
  },
  "family-protection": {
    slug: "family-protection",
    parentSlug: "/insurance",
    parentLabel: "Insurance",
    eyebrow: "Insurance Solutions",
    title: "Family Protection",
    tagline: "Comprehensive plans that put your family's long-term security first.",
    intro:
      "Family protection is about layering the right coverage — life, disability, and living benefits — so your family stays financially healthy no matter what happens. We help you build a plan that fits your budget and grows with your needs.",
    whoFor: [
      "Young families building a foundation",
      "Blended families with complex needs",
      "Families of veterans transitioning to civilian life",
    ],
    benefits: [
      "Coordinated life + disability coverage",
      "Living benefits for critical illness",
      "Coverage that scales with your family's growth",
      "One consultation, one advisor, one plan",
    ],
    faqs: [
      { q: "How much life insurance does a family really need?", a: "A common starting point is 10× annual income for the primary earner — but the right answer depends on debts, kids' ages, and goals. We'll help you calculate a real number, not a guess." },
    ],
  },
  "retirement": {
    slug: "retirement",
    parentSlug: "/insurance",
    parentLabel: "Insurance",
    eyebrow: "Insurance Solutions",
    title: "Retirement-Focused Solutions",
    tagline: "Insurance strategies aligned with your retirement and legacy goals.",
    intro:
      "For clients approaching or in retirement, the right insurance strategy can provide protection, tax advantages, and a source of stable income. We help you understand what's a fit — and what isn't.",
    whoFor: [
      "Clients within 10 years of retirement",
      "Retirees with legacy or estate planning goals",
      "Adults looking for tax-advantaged growth alongside investments",
    ],
    benefits: [
      "Tax-advantaged accumulation options",
      "Predictable income solutions",
      "Legacy planning tools",
      "Coordinated with your existing retirement plan",
    ],
    faqs: [
      { q: "Should I use insurance as an investment?", a: "Insurance is protection first. Some products have accumulation features that can complement investments — but they aren't a replacement. We'll be honest about the tradeoffs." },
    ],
  },
  "medicare": {
    slug: "medicare",
    parentSlug: "/insurance",
    parentLabel: "Insurance",
    eyebrow: "Insurance Solutions",
    title: "Medicare",
    tagline: "Original, Advantage, Supplement, and Part D — explained in plain English.",
    intro:
      "Turning 65 or already enrolled? Medicare has more moving parts than most people expect: Parts A and B, Advantage (Part C), Supplement (Medigap) plans, and Part D prescription drug coverage. We help you sort through your options so you understand exactly what you're getting — and what you're paying for.",
    whoFor: [
      "Adults approaching 65 (initial enrollment window)",
      "Current Medicare beneficiaries reviewing their plan annually",
      "Retiring veterans coordinating Medicare with VA care",
      "Adults with disabilities eligible for Medicare early",
    ],
    benefits: [
      "Plain-language walkthrough of Parts A, B, C, and D",
      "Medigap vs. Medicare Advantage comparison",
      "Annual Election Period (AEP) plan reviews",
      "Coordination with existing VA, employer, or retiree coverage",
    ],
    faqs: [
      { q: "Should I take Medicare Advantage or Original Medicare + Medigap?", a: "There's no universal answer — it depends on your doctors, medications, travel patterns, and budget. We compare specific plans available in your area so you can decide with full information." },
      { q: "Do I need Medicare if I have VA health care?", a: "Many veterans enroll in Medicare Part A (usually premium-free) as a safety net, even if VA is their primary care. We help you weigh the tradeoffs based on your specific situation." },
      { q: "When can I switch plans?", a: "Most changes happen during the Annual Election Period (October 15 – December 7). Some situations qualify for a Special Enrollment Period." },
    ],
  },
  "health-insurance": {
    slug: "health-insurance",
    parentSlug: "/insurance",
    parentLabel: "Insurance",
    eyebrow: "Insurance Solutions",
    title: "Health Insurance",
    tagline: "Individual, family, and marketplace plans — matched to your needs and budget.",
    intro:
      "Whether you're between jobs, self-employed, or comparing marketplace options, choosing the right health insurance plan is one of the more consequential financial decisions you make each year. We help you compare networks, deductibles, out-of-pocket maximums, and premium subsidies so you can pick a plan with confidence.",
    whoFor: [
      "Self-employed individuals and small business owners",
      "Adults between jobs (post-COBRA)",
      "Families shopping for a new marketplace plan",
      "Veterans and dependents needing coverage alongside or outside VA care",
    ],
    benefits: [
      "Marketplace (ACA), private, and short-term plan comparisons",
      "Subsidy and cost-sharing eligibility review",
      "Network and prescription formulary checks",
      "HSA-eligible plan guidance",
    ],
    faqs: [
      { q: "Can I qualify for a subsidy on the marketplace?", a: "Most people qualify for at least some level of premium tax credit or cost-sharing reduction. We help you estimate what you're eligible for before you enroll." },
      { q: "What if I miss Open Enrollment?", a: "Certain life events — losing coverage, moving, marriage, birth of a child — qualify you for a Special Enrollment Period outside the normal window." },
      { q: "Do you help with short-term health plans?", a: "Yes, when they fit the situation. Short-term plans can bridge gaps between coverage but aren't a full substitute for ACA-compliant insurance. We're upfront about the tradeoffs." },
    ],
  },
};

export const INSURANCE_PRODUCTS_LIST = Object.values(INSURANCE_PRODUCTS);
